import {Injectable} from '@angular/core';
import {SpatialUtilService} from '../../design/services/spatial-util.service';

@Injectable({
    providedIn: 'root'
})
export class SpatialSequenceService {
    title = 'SpatialSequenceService';

    private sequenceMap: Map<string, number> = new Map();

    constructor(private spatialUtilService: SpatialUtilService) {
        this.spatialUtilService.getFeatureClasses().forEach(featureClass => this.sequenceMap.set(this.getSequenceMapKey(featureClass), 0));
    }

    getSequence(count: number, featureClassOrLayer: string | object) {
        if (count === 0) {
            return [];
        }

        const featureClass = this.spatialUtilService.getFeatureClass(featureClassOrLayer);
        console.debug(`${this.title} - getSpatialSequence() - feature class: ${featureClass}, count: ${count}`);

        const sequenceMapKey = this.getSequenceMapKey(featureClass);
        if (this.canResetSequence(sequenceMapKey)) {
            this.resetSequence(sequenceMapKey);
        }

        const numberSequence = new Array(count).fill(0).map((v, i) => this.sequenceMap.get(sequenceMapKey) + i + 1);

        this.sequenceMap.set(sequenceMapKey, numberSequence[numberSequence.length - 1]);

        return numberSequence;
    }

    getAffectedLotSequence(count: number) {
        return this.getSequence(count, 'affectedlot');
    }

    getAngleSequence(count: number) {
        return this.getSequence(count, 'angle');
    }

    getAnnotationSequence(count: number) {
        return this.getSequence(count, 'annotation');
    }

    getClippingSequence(count: number) {
        return this.getSequence(count, 'clipping');
    }

    getConstructLineSequence(count: number) {
        return this.getSequence(count, 'constructline');
    }

    getConstructPointSequence(count: number) {
        return this.getSequence(count, 'constructpoint');
    }

    getControlPointSequence(count: number) {
        return this.getSequence(count, 'point');
    }

    getEnlargementSequence(count: number) {
        return this.getSequence(count, 'enlargement');
    }

    getInterestSequence(count: number) {
        return this.getSequence(count, 'interest');
    }

    getLineSequence(count: number) {
        return this.getSequence(count, 'line');
    }

    getLotSequence(count: number) {
        return this.getSequence(count, 'lot');
    }

    getPointSequence(count: number) {
        return this.getSequence(count, 'point');
    }

    getPolygonSequence(count: number) {
        return this.getSequence(count, 'polygon');
    }

    getPolylineSequence(count: number) {
        return this.getSequence(count, 'line');
    }

    getPrintAreaSequence(count: number) {
        return this.getSequence(count, 'printarea');
    }

    getRoadSequence(count: number) {
        return this.getSequence(count, 'road');
    }

    sync(featureClass?: string) {
        console.debug(`${this.title} - sync() - feature class:`, featureClass);

        if (featureClass) {
            const sequenceMapKey = this.getSequenceMapKey(featureClass);
            this.sequenceMap.set(sequenceMapKey, this.getHighestId(sequenceMapKey));
        } else {
            for (const key of this.sequenceMap.keys()) {
                this.sync(key);
            }
        }
    }

    init() {
        console.debug(`${this.title} - init()`);

        for (const key of this.sequenceMap.keys()) {
            this.sequenceMap.set(key, 0);
        }
    }

    private getSequenceMapKey(featureClass: string) {
        switch (featureClass) {
            case 'controlpoint':
                return 'point';
            case 'polyline':
                return 'line';
            default:
                return featureClass;
        }
    }

    private getHighestId(sequenceMapKey: string) {
        const featureIds = [];

        if (sequenceMapKey === 'point') {
            featureIds.push(...this.spatialUtilService.getFeatureIds('point'), ...this.spatialUtilService.getFeatureIds('controlpoint'));
        } else if (sequenceMapKey === 'line') {
            featureIds.push(...this.spatialUtilService.getFeatureIds('line'), ...this.spatialUtilService.getFeatureIds('polyline'));
        } else {
            featureIds.push(...this.spatialUtilService.getFeatureIds(sequenceMapKey));
        }

        return featureIds.sort((a, b) => b - a)[0] || 0;
    }

    private canResetSequence(sequenceMapKey: string) {
        if (this.getHighestId(sequenceMapKey) !== 0) {
            return false;
        }

        const editedIds = [];
        if (sequenceMapKey === 'point') {
            editedIds.push(
                ...[...this.spatialUtilService.getAddedDeletedIds('point'), ...this.spatialUtilService.getAddedDeletedIds('controlpoint')]
                    .sort((a, b) => a - b)
                    .filter((v, i, a) => i === a.indexOf(v))
            );
        } else if (sequenceMapKey === 'line') {
            editedIds.push(
                ...[...this.spatialUtilService.getAddedDeletedIds('line'), ...this.spatialUtilService.getAddedDeletedIds('polyline')]
                    .sort((a, b) => a - b)
                    .filter((v, i, a) => i === a.indexOf(v))
            );
        } else {
            editedIds.push(...this.spatialUtilService.getAddedDeletedIds(sequenceMapKey));
        }

        const lastSequenceId = this.sequenceMap.get(sequenceMapKey);

        return lastSequenceId === editedIds.length && lastSequenceId === (editedIds.length ? editedIds[editedIds.length - 1] : 0);
    }

    private resetSequence(sequenceMapKey: string) {
        this.sequenceMap.set(sequenceMapKey, 0);
        if (sequenceMapKey === 'point') {
            this.spatialUtilService.clearAddedDeletedIds('point');
            this.spatialUtilService.clearAddedDeletedIds('controlpoint');
        } else if (sequenceMapKey === 'line') {
            this.spatialUtilService.clearAddedDeletedIds('line');
            this.spatialUtilService.clearAddedDeletedIds('polyline');
        } else {
            this.spatialUtilService.clearAddedDeletedIds(sequenceMapKey);
        }
    }
}
